import React, { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { dueNow, formatClockMin, msToMin, runProgress } from '@battleops/shared';
import { useRoutines } from '@battleops/client/src/context/ProcessContext';
import { useTasks } from '@battleops/client/src/context/TaskContext';
import { useLocalCollection } from '@battleops/client/src/data/useLocal';
import { openRoutineMode } from '../lib/routineMode';
import { useNow, useTaskModal } from '../hooks';
import Icon from './Icon';
import s from './DueNow.module.css';

// "Due now" on the All Home (Lj, Oct 11: routines / reminders as their own card, only when one is due, only on All):
// the routine going, routines ready or missed, reminders that rang and aren't answered — Start / Open. Nothing due =
// nothing drawn. Shared dueNow (the phone's DueNowCard reads the same).
export default function DueNow() {
  const navigate = useNavigate();
  const details = useTaskModal();
  const { today, activeRun, startRoutine } = useRoutines();
  const { tasks } = useTasks();
  const reminders = useLocalCollection('reminders');
  const now = useNow(60000);
  const x = useMemo(() => dueNow({ routines: today(now), activeRun, tasks, reminders, now }), [today, activeRun, tasks, reminders, now]);
  if (!x.n) return null;
  const go = (it) => {
    if (it.kind === 'run') return openRoutineMode();
    if (it.kind === 'routine') { try { startRoutine(it.routine.id, it.run?.n ? { n: it.run.n } : {}); } catch (e) { console.warn(e); return undefined; } return openRoutineMode(); }
    if (it.task) return details.open(it.task.id);
    const l = it.link;
    if (l?.type === 'approval') return navigate('/approvals');
    if (l?.type === 'task') return details.open(l.id);
    if (l?.type === 'project') return navigate(`/projects/${l.id}`);
    if (l?.type === 'routine' || l?.type === 'process') return navigate('/routines');
    return navigate('/reminders');
  };
  return (
    <section className={s.card} aria-label={`Due now: ${x.n}`}>
      <div className={s.head}><span className={s.dot} /><h2>Due now</h2><span className={s.count}>{x.n}</span></div>
      <div className={s.list}>
        {x.items.slice(0, 6).map((it) => {
          const p = it.kind === 'run' ? runProgress(it.run) : null;
          const tone = it.kind === 'run' ? (it.paused ? 'var(--c-warning)' : 'var(--c-success)') : it.kind === 'routine' ? it.color || 'var(--c-accent)' : 'var(--c-gold)';
          const label = it.kind === 'run' ? (it.paused ? 'Back in' : 'Open') : it.kind === 'routine' ? 'Start' : 'Open';
          return (
            <button key={it.id} type="button" className={s.row} style={{ '--t': tone }} onClick={() => go(it)}>
              <span className={s.icon}>{it.icon || <Icon name={it.kind === 'run' ? (it.paused ? 'pause' : 'play') : it.kind === 'routine' ? 'repeat' : 'bell'} size={16} />}</span>
              <span className={s.text}>
                <b>{it.title}</b>
                <span data-missed={it.state === 'missed' || undefined}>{it.kind === 'run' ? `${it.sub} · step ${Math.min(p.done + p.skipped + 1, p.total)} of ${p.total}` : `${it.sub} · ${formatClockMin(msToMin(it.at))}`}</span>
              </span>
              <span className={s.btn} data-fill={it.kind === 'routine' || undefined}>{label}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
